import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { ApiService } from '../../../../core/services/api.service';
import {
  ParsedFileEntry,
  ParsedFileVersion,
  ApprovalStatus,
} from '../../../../core/models/file-browser.models';
import { ReviewDialogComponent } from './review-dialog.component';

/** Parsed markdown output is stored under this prefix; the browser hides it. */
const PARSED_ROOT_PREFIX = 'Markdown/';

interface ExpandedFile {
  key: string;
  versions: ParsedFileVersion[];
  loading: boolean;
  error: string | null;
}

@Component({
  selector: 'app-parsed-file-browser',
  standalone: true,
  imports: [CommonModule, ReviewDialogComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './parsed-file-browser.component.html',
})
export class ParsedFileBrowserComponent implements OnInit {
  @Output() notification = new EventEmitter<string>();
  @Output() initialParsedKeyConsumed = new EventEmitter<void>();

  @Input() set initialParsedKey(key: string | null) {
    if (!key) return;
    const parts = key.split('/');
    parts.pop();
    let prefix = parts.length ? parts.join('/') + '/' : '';
    // Parsed markdown lives under a Markdown/ root that the browser hides; keep
    // navigation/breadcrumbs relative to it. The full key is still used to open
    // the review dialog below.
    if (prefix.startsWith(PARSED_ROOT_PREFIX)) {
      prefix = prefix.slice(PARSED_ROOT_PREFIX.length);
    }
    this._initialKeyLoaded = true;
    this._pendingOpenKey = key;
    this.load(prefix);
    this.initialParsedKeyConsumed.emit();
  }

  private api = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);

  private _initialKeyLoaded = false;
  private _pendingOpenKey: string | null = null;

  currentPrefix = '';
  breadcrumbs: { label: string; prefix: string }[] = [];
  folders: string[] = [];
  files: ParsedFileEntry[] = [];
  loading = false;
  loadError: string | null = null;

  expanded: Map<string, ExpandedFile> = new Map();

  // Review dialog state
  reviewOpen = false;
  reviewFile: { parsedS3Key: string; versionId: string | null } | null = null;

  ngOnInit(): void {
    if (!this._initialKeyLoaded) {
      this.load('');
    }
  }

  load(prefix: string): void {
    this.currentPrefix = prefix;
    this.buildBreadcrumbs(prefix);
    this.loading = true;
    this.loadError = null;
    this.expanded.clear();
    this.cdr.markForCheck();

    this.api.listParsedFiles(prefix).subscribe({
      next: result => {
        this.folders = result.folders;
        this.files = result.files;
        this.loading = false;
        if (this._pendingOpenKey) {
          const key = this._pendingOpenKey;
          this._pendingOpenKey = null;
          this.openReview(key, null);
        }
        this.cdr.markForCheck();
      },
      error: err => {
        this.loadError = err?.message || 'Failed to load parsed files';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  navigateToFolder(folderPrefix: string): void {
    this.load(folderPrefix);
  }

  navigateUp(): void {
    const parts = this.currentPrefix.replace(/\/$/, '').split('/');
    parts.pop();
    this.load(parts.length ? parts.join('/') + '/' : '');
  }

  toggleExpand(file: ParsedFileEntry): void {
    if (this.expanded.has(file.key)) {
      this.expanded.delete(file.key);
      this.cdr.markForCheck();
      return;
    }

    const entry: ExpandedFile = { key: file.key, versions: [], loading: true, error: null };
    this.expanded.set(file.key, entry);
    this.cdr.markForCheck();

    this.api.getParsedFileVersions(file.key).subscribe({
      next: versions => {
        entry.versions = versions;
        entry.loading = false;
        this.cdr.markForCheck();
      },
      error: err => {
        entry.error = err?.message || 'Failed to load versions';
        entry.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  isExpanded(key: string): boolean {
    return this.expanded.has(key);
  }

  getExpanded(key: string): ExpandedFile | undefined {
    return this.expanded.get(key);
  }

  openReview(parsedS3Key: string, versionId: string | null): void {
    this.reviewFile = { parsedS3Key, versionId };
    this.reviewOpen = true;
    this.cdr.markForCheck();
  }

  closeReview(): void {
    this.reviewOpen = false;
    this.reviewFile = null;
    this.cdr.markForCheck();
  }

  onRevisionSaved(parsedS3Key: string): void {
    this.closeReview();
    this.notification.emit('Revision saved and marked as approved');
    // Refresh version list if the file panel is open
    const file = this.files.find(f => f.key === parsedS3Key);
    if (file && this.expanded.has(parsedS3Key)) {
      this.expanded.delete(parsedS3Key);
      this.toggleExpand(file);
    }
  }

  approvalLabel(status: ApprovalStatus): string {
    switch (status) {
      case 'manually_approved': return 'Approved';
      case 'auto_approved':     return 'Auto-approved';
      case 'needs_review':      return 'Requires Review';
    }
  }

  approvalClasses(status: ApprovalStatus): string {
    switch (status) {
      case 'manually_approved': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
      case 'auto_approved':     return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
      case 'needs_review':      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
    }
  }

  fileName(key: string): string {
    /* c8 ignore next */
    return key.split('/').pop() ?? key;
  }

  folderName(folder: string): string {
    /* c8 ignore next */
    return folder.replace(/\/$/, '').split('/').pop() ?? folder;
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatDate(iso: string): string {
    return new Date(iso).toLocaleString();
  }

  formatConfidence(v: number | null): string {
    return v === null ? '—' : `${v.toFixed(1)}%`;
  }

  private buildBreadcrumbs(prefix: string): void {
    this.breadcrumbs = [{ label: 'Parsed Files', prefix: '' }];
    if (!prefix) return;
    const parts = prefix.replace(/\/$/, '').split('/');
    let acc = '';
    for (const part of parts) {
      acc += part + '/';
      this.breadcrumbs.push({ label: part, prefix: acc });
    }
  }
}
